import { loadAppearance } from './clinicTheme';

let accent = [31, 77, 64];

function applyAccent(color) {
  const match = /^#([0-9a-f]{6})$/i.exec(String(color || '').trim());
  const value = match ? match[1] : '1f4d40';
  const num = parseInt(value, 16);
  accent = [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}
const INK = [26, 26, 26];
const MUTED = [90, 104, 99];
const LINE = [208, 215, 212];
const WASH = [244, 247, 246];
function pdfImageFormat(dataUrl) {
  const mime = String(dataUrl || '').slice(5, String(dataUrl || '').indexOf(';')).toLowerCase();
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'JPEG';
  if (mime === 'image/webp') return 'WEBP';
  return 'PNG';
}

const MARGIN = 14;
const CONTENT_W = 182;
const CONTENT_BOTTOM = 274;

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function doctorOf(appointment) {
  const populated = appointment.DOCTOR_ID && typeof appointment.DOCTOR_ID === 'object' ? appointment.DOCTOR_ID : null;
  const nested = appointment.doctor && typeof appointment.doctor === 'object' ? appointment.doctor : null;
  const name = populated?.DOCTOR_NAME || nested?.DOCTOR_NAME || appointment.DOCTOR_NAME || '';
  return {
    name: /^dr\.?\s/i.test(name) ? name : (name ? `Dr. ${name}` : ''),
    specialization: populated?.SPECIALIZATION || nested?.SPECIALIZATION || '',
    clinic: populated?.CLINIC_LOCATION || nested?.CLINIC_LOCATION || ''
  };
}

function vitalPairs(vitals) {
  if (!vitals) return [];
  const pairs = [];
  if (vitals.BLOOD_PRESSURE) pairs.push(['Blood pressure', `${vitals.BLOOD_PRESSURE} mmHg`]);
  if (vitals.PULSE != null && vitals.PULSE !== '') pairs.push(['Pulse', `${vitals.PULSE} bpm`]);
  if (vitals.TEMPERATURE != null && vitals.TEMPERATURE !== '') pairs.push(['Temperature', `${vitals.TEMPERATURE} °F`]);
  if (vitals.RESPIRATORY_RATE != null && vitals.RESPIRATORY_RATE !== '') pairs.push(['Respiratory rate', `${vitals.RESPIRATORY_RATE}/min`]);
  if (vitals.SPO2 != null && vitals.SPO2 !== '') pairs.push(['SpO2', `${vitals.SPO2}%`]);
  if (vitals.WEIGHT != null && vitals.WEIGHT !== '') pairs.push(['Weight', `${vitals.WEIGHT} kg`]);
  if (vitals.HEIGHT != null && vitals.HEIGHT !== '') pairs.push(['Height', `${vitals.HEIGHT} cm`]);
  const weight = Number(vitals.WEIGHT);
  const height = Number(vitals.HEIGHT);
  if (weight > 0 && height > 0) {
    const meters = height / 100;
    pairs.push(['BMI', (weight / (meters * meters)).toFixed(1)]);
  }
  return pairs;
}

function medicineRows(appointment) {
  return (appointment.DR_MEDICINES || []).map((item, index) => {
    const dose = [item.MEDICINE_DOSAGE ?? item.DOSAGE, item.DOSAGE_UNIT].filter((part) => part != null && part !== '').join(' ');
    const duration = item.DURATION ? `${item.DURATION}${String(item.DURATION).toLowerCase().includes('day') ? '' : ' days'}` : '';
    const times = Array.isArray(item.DOSAGE_TIME) ? item.DOSAGE_TIME.filter(Boolean).join(', ') : '';
    const note = [times && `Time: ${times}`, item.INSTRUCTIONS].filter(Boolean).join('  ·  ');
    return {
      cells: [
        String(index + 1),
        item.MEDICINE_ID?.MEDICINE_NAME || item.MEDICINE_NAME || 'Medicine',
        dose,
        item.DOSAGE_FREQUENCY || item.FREQUENCY || '',
        duration,
        item.MEDICINE_ROUTE || ''
      ],
      note
    };
  });
}

function procedureRows(appointment) {
  return (appointment.DR_PROCEDURES || []).map((item, index) => {
    const note = [item.THERAPIST_NAME && `Therapist: ${item.THERAPIST_NAME}`, item.PROCEDURE_STATUS].filter(Boolean).join('  ·  ');
    return {
      cells: [
        String(index + 1),
        item.PROCEDURE_NAME || item.PROCEDURE_ID?.PROCEDURE_NAME || 'Procedure',
        formatDate(item.PROCEDURE_DATE || item.SCHEDULED_DATE),
        item.PROCEDURE_SLOT || (item.DURATION ? `${item.DURATION} min` : ''),
        item.ROOM_NAME || ''
      ],
      note
    };
  });
}

function labRows(appointment) {
  return (appointment.DR_LAB_TESTS || []).map((item, index) => {
    const note = [
      item.FASTING_REQUIRED ? 'Fasting required' : '',
      item.INSTRUCTIONS,
      item.PREPARATION_NOTES && `Preparation: ${item.PREPARATION_NOTES}`
    ].filter(Boolean).join('  ·  ');
    return {
      cells: [
        String(index + 1),
        item.LAB_TEST_ID?.TEST_NAME || item.TEST_NAME || 'Test',
        item.LAB_TEST_ID?.TEST_CATEGORY || '',
        item.TEST_STATUS || '',
        formatDate(item.TEST_DATE)
      ],
      note
    };
  });
}

function frame(doc) {
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.4);
  doc.rect(8, 8, 194, 281);
  doc.setLineWidth(0.2);
}

function continuedHeader(doc, patientName) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text('PRESCRIPTION', MARGIN, 16);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  if (patientName) doc.text(patientName, MARGIN + 34, 16);
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, 19, MARGIN + CONTENT_W, 19);
  doc.setLineWidth(0.2);
  doc.setTextColor(...INK);
  return 24;
}

function nextPage(doc, state) {
  doc.addPage();
  frame(doc);
  return continuedHeader(doc, state.patientName);
}

function sectionHeading(doc, y, title) {
  doc.setFillColor(...accent);
  doc.rect(MARGIN, y, CONTENT_W, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(String(title).toUpperCase(), MARGIN + 2, y + 4.8);
  doc.setTextColor(...INK);
  return y + 9;
}

function fitLogo(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl || typeof Image === 'undefined') {
      resolve(dataUrl ? { width: 36, height: 14 } : null);
      return;
    }
    const image = new Image();
    image.onload = () => {
      const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 2;
      let width = 48;
      let height = width / ratio;
      if (height > 16) {
        height = 16;
        width = height * ratio;
      }
      resolve({ width: Math.max(width, 12), height: Math.max(height, 8) });
    };
    image.onerror = () => resolve({ width: 36, height: 14 });
    image.src = dataUrl;
  });
}

function drawHeader(doc, y, { logo, logoBox, issued }) {
  let textX = MARGIN;
  let block = 16;
  if (logo && logoBox) {
    try {
      doc.addImage(logo, pdfImageFormat(logo), MARGIN, y, logoBox.width, logoBox.height);
      textX = MARGIN + logoBox.width + 4;
      block = Math.max(block, logoBox.height);
    } catch (error) {
      textX = MARGIN;
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...accent);
  doc.text('PRESCRIPTION', textX, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`Issued ${issued}`, textX, y + 13);
  const bottom = y + block;
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, bottom + 2, MARGIN + CONTENT_W, bottom + 2);
  doc.setLineWidth(0.2);
  doc.setTextColor(...INK);
  return bottom + 7;
}

function drawVisit(doc, y, appointment) {
  const parts = [
    formatDate(appointment.APPOINTMENT_DATE) ? `Visit ${formatDate(appointment.APPOINTMENT_DATE)}` : '',
    appointment.APPOINTMENT_SLOT || '',
    appointment.CONSULTATION_MODE || '',
    appointment.APPOINTMENT_TYPE || ''
  ].filter(Boolean);
  if (!parts.length) return y;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(parts.join('    ·    '), MARGIN, y);
  doc.setTextColor(...INK);
  return y + 6;
}

function drawIdentity(doc, y, appointment) {
  const patient = appointment.PATIENT_ID || {};
  const doctor = doctorOf(appointment);
  const height = 32;
  doc.setDrawColor(...LINE);
  doc.setFillColor(...WASH);
  doc.roundedRect(MARGIN, y, CONTENT_W, height, 1.2, 1.2, 'FD');
  doc.line(MARGIN + CONTENT_W / 2, y + 3, MARGIN + CONTENT_W / 2, y + height - 3);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...accent);
  doc.text('PATIENT', MARGIN + 3, y + 6);
  doc.text('DOCTOR', MARGIN + CONTENT_W / 2 + 3, y + 6);

  doc.setTextColor(...INK);
  doc.setFontSize(12);
  doc.text(patient.PATIENT_NAME || 'Patient', MARGIN + 3, y + 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const meta = [
    patient.PATIENT_ID,
    patient.PATIENT_AGE != null && patient.PATIENT_AGE !== '' ? `${patient.PATIENT_AGE} years` : '',
    patient.PATIENT_GENDER || ''
  ].filter(Boolean).join('   ·   ');
  if (meta) doc.text(meta, MARGIN + 3, y + 19);
  if (patient.PATIENT_PHONE) doc.text(String(patient.PATIENT_PHONE), MARGIN + 3, y + 25);

  const dx = MARGIN + CONTENT_W / 2 + 3;
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const doctorName = doctor.name || 'Doctor';
  doc.text(doc.splitTextToSize(doctorName, CONTENT_W / 2 - 8)[0], dx, y + 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  let line = y + 19;
  if (doctor.specialization) {
    doc.text(doc.splitTextToSize(doctor.specialization, CONTENT_W / 2 - 8)[0], dx, line);
    line += 5;
  }
  if (doctor.clinic) {
    doc.text(doc.splitTextToSize(doctor.clinic, CONTENT_W / 2 - 8)[0], dx, line);
  }
  doc.setTextColor(...INK);
  return y + height + 6;
}

function drawVitals(doc, state) {
  const pairs = vitalPairs(state.appointment.PATIENT_ID?.VITALS);
  if (!pairs.length) return;
  const cols = 4;
  const rows = Math.ceil(pairs.length / cols);
  const cellW = CONTENT_W / cols;
  const cellH = 12;
  if (state.y + 8 + rows * cellH > CONTENT_BOTTOM) state.y = nextPage(doc, state);
  state.y = sectionHeading(doc, state.y, 'Vitals');
  pairs.forEach((pair, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const x = MARGIN + col * cellW;
    const y = state.y + row * cellH;
    doc.setDrawColor(...LINE);
    doc.setFillColor(...WASH);
    doc.rect(x, y, cellW, cellH, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(pair[0], x + 1.6, y + 4.2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...INK);
    doc.text(String(pair[1]), x + 1.6, y + 9);
  });
  state.y += rows * cellH + 5;
}

function drawProse(doc, state, title, text) {
  const value = String(text || '').trim();
  if (!value) return;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(value, CONTENT_W - 6);
  let index = 0;
  let titled = false;
  while (index < lines.length) {
    if (state.y + 18 > CONTENT_BOTTOM) state.y = nextPage(doc, state);
    state.y = sectionHeading(doc, state.y, titled ? `${title} continued` : title);
    titled = true;
    const fit = Math.max(1, Math.floor((CONTENT_BOTTOM - state.y - 3) / 4.4));
    const slice = lines.slice(index, index + fit);
    const boxH = slice.length * 4.4 + 4;
    doc.setDrawColor(...LINE);
    doc.setFillColor(255, 255, 255);
    doc.rect(MARGIN, state.y, CONTENT_W, boxH, 'S');
    doc.setTextColor(...INK);
    doc.text(slice, MARGIN + 3, state.y + 4.6);
    state.y += boxH + 5;
    index += slice.length;
  }
}

function measureRow(doc, widths, row) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const cells = widths.map((width, index) => doc.splitTextToSize(String(row.cells[index] || ''), Math.max(width - 3, 4)));
  const lineCount = Math.max(...cells.map((cell) => Math.max(cell.length, 1)), 1);
  const note = String(row.note || '').trim();
  const noteLines = note ? doc.splitTextToSize(note, CONTENT_W - 6) : [];
  let height = lineCount * 3.8 + 2.4;
  if (noteLines.length) height += noteLines.length * 3.6 + 1.2;
  return { cells, lineCount, noteLines, height };
}

function drawTable(doc, state, title, columns, rows) {
  if (!rows.length) return;
  const widths = columns.map((column) => column.width);
  const drawHead = () => {
    doc.setFillColor(...accent);
    doc.rect(MARGIN, state.y, CONTENT_W, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    let x = MARGIN;
    columns.forEach((column, index) => {
      doc.text(column.label, x + 1.5, state.y + 4.6);
      x += widths[index];
    });
    doc.setTextColor(...INK);
    state.y += 7;
  };
  const first = measureRow(doc, widths, rows[0]);
  if (state.y + 16 + first.height > CONTENT_BOTTOM) state.y = nextPage(doc, state);
  state.y = sectionHeading(doc, state.y, title);
  drawHead();
  rows.forEach((row) => {
    const measured = measureRow(doc, widths, row);
    if (state.y + measured.height > CONTENT_BOTTOM) {
      state.y = nextPage(doc, state);
      drawHead();
    }
    doc.setDrawColor(...LINE);
    doc.rect(MARGIN, state.y, CONTENT_W, measured.height, 'S');
    let x = MARGIN;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...INK);
    measured.cells.forEach((cell, index) => {
      if (cell[0]) doc.text(cell, x + 1.5, state.y + 3.8);
      x += widths[index];
    });
    if (measured.noteLines.length) {
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...MUTED);
      doc.text(measured.noteLines, MARGIN + 3, state.y + measured.lineCount * 3.8 + 3.2);
      doc.setTextColor(...INK);
    }
    state.y += measured.height;
  });
  state.y += 5;
}

function drawSignature(doc, state, doctorName) {
  if (state.y + 18 > CONTENT_BOTTOM) state.y = nextPage(doc, state);
  const x = MARGIN + CONTENT_W - 62;
  state.y += 8;
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.4);
  doc.line(x, state.y, x + 58, state.y);
  doc.setLineWidth(0.2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text('Signature', x, state.y + 4);
  if (doctorName) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...INK);
    doc.text(doctorName, x, state.y + 9);
  }
}

function stampFooters(doc) {
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, 278, MARGIN + CONTENT_W, 278);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('Valid for 7 days from the date of issue.', MARGIN, 283);
    doc.text(`Page ${page} of ${pages}`, MARGIN + CONTENT_W, 283, { align: 'right' });
    doc.setTextColor(...INK);
    doc.setLineWidth(0.2);
  }
}

export async function renderPrescription(doc, appointment, logo, color) {
  applyAccent(color);
  const logoBox = await fitLogo(logo);
  const patientName = appointment.PATIENT_ID?.PATIENT_NAME || '';
  const state = { y: 14, appointment, patientName };
  frame(doc);
  state.y = drawHeader(doc, state.y, {
    logo,
    logoBox,
    issued: formatDate(new Date())
  });
  state.y = drawVisit(doc, state.y, appointment);
  state.y = drawIdentity(doc, state.y, appointment);
  drawVitals(doc, state);
  drawProse(doc, state, 'Symptoms', appointment.PATIENT_SYMPTOMS);
  drawProse(doc, state, 'Diagnosis', appointment.DIAGNOSIS);
  drawProse(doc, state, 'Advice', appointment.DOCTOR_COMMENTS);
  drawTable(doc, state, 'Medicines', [
    { label: '#', width: 10 },
    { label: 'Medicine', width: 62 },
    { label: 'Dose', width: 28 },
    { label: 'Frequency', width: 36 },
    { label: 'Duration', width: 24 },
    { label: 'Route', width: 22 }
  ], medicineRows(appointment));
  drawTable(doc, state, 'Procedures', [
    { label: '#', width: 10 },
    { label: 'Procedure', width: 70 },
    { label: 'Date', width: 32 },
    { label: 'Slot', width: 28 },
    { label: 'Room', width: 42 }
  ], procedureRows(appointment));
  drawTable(doc, state, 'Investigations', [
    { label: '#', width: 10 },
    { label: 'Test', width: 78 },
    { label: 'Category', width: 36 },
    { label: 'Status', width: 28 },
    { label: 'Date', width: 30 }
  ], labRows(appointment));
  if (appointment.FOLLOW_UP_DATE) {
    drawProse(doc, state, 'Follow-up', formatDate(appointment.FOLLOW_UP_DATE));
  }
  drawSignature(doc, state, doctorOf(appointment).name);
  stampFooters(doc);
}

export async function downloadPrescriptionPdf({ appointment, logo, color }) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const appearance = color ? null : await loadAppearance();
  await renderPrescription(doc, appointment, logo, color || (appearance && appearance.prescriptionColor));
  const patientId = appointment.PATIENT_ID?.PATIENT_ID || 'patient';
  const day = new Date().toISOString().split('T')[0];
  doc.save(`prescription_${patientId}_${day}.pdf`);
}
